import usePostBulkUploadImages, { ImageType } from '../../../services/queries/usePostBulkUploadImages'

/**
 * Fetches an image from a URL and converts it to a File object
 */
const fetchImageAsBlob = async (url: string): Promise<File> => {
  try {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`)
    const blob = await response.blob()
    const fileName = url.split('/').pop() || 'image.png'
    return new File([blob], fileName, { type: blob.type || 'image/png' })
  } catch (err) {
    throw new Error(`Failed to fetch image from ${url}: ${err}`)
  }
}

/**
 * Uploads logo images from public folder to /images/bulk endpoint
 * Returns the uploaded URLs for Apple and Google Wallet logos
 */
export const uploadLogoImages = async (
  brandId: string,
  bulkUploadImagesMutation: ReturnType<typeof usePostBulkUploadImages>
): Promise<{ appleWalletLogoUrl: string; googleWalletLogoUrl: string }> => {
  try {
    // Fetch logo images from public folder
    const appleLogoUrl = '/assets/Bonhams_Logo_Apple_Wallet.png'
    const googleLogoUrl = '/assets/Bonhams_Logo_Google_Wallet.png'

    const [appleLogoFile, googleLogoFile] = await Promise.all([
      fetchImageAsBlob(appleLogoUrl),
      fetchImageAsBlob(googleLogoUrl),
    ])

    // Upload both logos in parallel
    const [appleResponse, googleResponse] = await Promise.all([
      bulkUploadImagesMutation.mutateAsync({
        brandId,
        type: 'APPLE_WALLET_LOGO' as ImageType,
        files: [appleLogoFile],
      }),
      bulkUploadImagesMutation.mutateAsync({
        brandId,
        type: 'GOOGLE_WALLET_LOGO' as ImageType,
        files: [googleLogoFile],
      }),
    ])

    // Extract uploaded URLs from responses
    // Use path field first as it contains the format: brand/.../apple_wallet_logo/...
    const appleWalletLogoUrl =
      appleResponse.successfulUploads[0]?.path ||
      appleResponse.successfulUploads[0]?.imagePathCompressedAsJpeg ||
      appleResponse.successfulUploads[0]?.webpUrl ||
      ''

    const googleWalletLogoUrl =
      googleResponse.successfulUploads[0]?.path ||
      googleResponse.successfulUploads[0]?.imagePathCompressedAsJpeg ||
      googleResponse.successfulUploads[0]?.webpUrl ||
      ''

    if (!appleWalletLogoUrl || !googleWalletLogoUrl) {
      throw new Error('Failed to get uploaded logo URLs from server response')
    }

    return { appleWalletLogoUrl, googleWalletLogoUrl }
  } catch (err) {
    throw new Error(`Failed to upload logo images: ${(err as Error).message}`)
  }
}
